import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import store from "store";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
import { getCfdQuoteCurrencyUpper } from "@/components/Pages/Market/MarketList/cfdTabListSource";
import { isFiatQuoteCurrency } from "@/utils/getFiatCurrencyMap";

import Storage from "@/utils/storage";
import XtInputSearch from "@/components/Xt/Input/Search";

import tabCfgAry from "./tabs/tabCfgAry";
import CMPT_Tabs from "./tabs";
import CMPT_Sort from "./sort";
import CMPT_Container from "./container";
import CMPT_TopSearch from "./topSearch";

import styles from "./index.module.scss";

import { TabCfgProps } from "./tabs";

const { useTranslation } = Hooks;

interface Props extends HTMLAttributes<HTMLDivElement> {
  clsSearch?: string;
  onMarketSelect?: () => void;
}

const Main: React.FC<Props> = ({ className, clsSearch, onMarketSelect }) => {
  const t = useTranslation();
  const router = useRouter();

  const isCfd = useMemo(() => {
    const byRoute = router.pathname?.startsWith("/futures/") || router.asPath?.startsWith("/futures/");
    const byStore = !!store.market.name && store.market.name.toLowerCase().endsWith("_cfd");
    return byRoute || byStore;
  }, [router.pathname, router.asPath, store.market.name]);

  const isFiatContract = useMemo(() => {
    if (!isCfd) return false;
    const { name } = store.market;
    if (!name) return false;
    return isFiatQuoteCurrency(getCfdQuoteCurrencyUpper(name));
  }, [isCfd, store.market.name]);

  const activeFiatQuote = useMemo(() => {
    if (!isFiatContract) return null;
    const q = getCfdQuoteCurrencyUpper(store.market.name);
    return q ? q.toLowerCase() : null;
  }, [isFiatContract, store.market.name]);

  const [keyword, seKeyword] = useState<string>("");
  const [isSearchInputFocus, setIsSearchInputFocus] = useState<WithUndefined<boolean>>(undefined);
  const isTopSearchVisible = useMemo(() => !(keyword || !isSearchInputFocus), [keyword, isSearchInputFocus]);

  const inputRef = useRef<HTMLInputElement>(null);
  const refIsFocus = useRef(false);

  const handleSearchInputFocus = useCallback(() => {
    setIsSearchInputFocus(true);
  }, []);

  const handleSearchInputBlur = useCallback(() => {
    if (!refIsFocus.current || !inputRef.current) {
      setIsSearchInputFocus(false);
    } else {
      refIsFocus.current = false;
      inputRef.current.focus();
    }
  }, []);

  const setSearchInputFocus = useCallback(() => {
    refIsFocus.current = true;
  }, []);

  const [tabCfg, setTabCfg] = useState<TabCfgProps>(tabCfgAry[1]);
  const firstFiatTab = useMemo(
    () => (isCfd && isFiatContract ? tabCfgAry.find((item) => item.key === "fiat") ?? null : null),
    [isCfd, isFiatContract]
  );

  useEffect(() => {
    const nextTabCfg = Storage.get("marketGroup");

    if (nextTabCfg && nextTabCfg.key) {
      if (!isCfd && nextTabCfg.key === "fiat") {
        setTabCfg(tabCfgAry[1]);
        Storage.set("marketGroup", tabCfgAry[1]);
        return;
      }
      if (isCfd && !isFiatContract && nextTabCfg.key === "fiat") {
        setTabCfg(tabCfgAry[1]);
        Storage.set("marketGroup", tabCfgAry[1]);
        return;
      }
      if (isCfd && isFiatContract && firstFiatTab) {
        const notFiatTab = nextTabCfg.key !== "fiat";
        const isCommoditiesOrStocks = nextTabCfg.key === "commodities" || nextTabCfg.key === "stocks";
        if (notFiatTab && nextTabCfg.key !== "user" && !isCommoditiesOrStocks) {
          setTabCfg(firstFiatTab);
          Storage.set("marketGroup", firstFiatTab);
          return;
        }
      }
      if (nextTabCfg.key !== "user" || store.user.isLogin) setTabCfg(nextTabCfg);
    } else {
      if (isCfd && isFiatContract && firstFiatTab) {
        setTabCfg(firstFiatTab);
        Storage.set("marketGroup", firstFiatTab);
      } else if (!isCfd || !isFiatContract) {
        setTabCfg(tabCfgAry[1]);
        Storage.set("marketGroup", tabCfgAry[1]);
      }
    }
  }, [isCfd, isFiatContract, firstFiatTab]);

  const onTabChange = (nextTabCfg) => {
    setTabCfg(nextTabCfg);
    Storage.set("marketGroup", nextTabCfg);
  };

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [isShowVolume, setIsShowVolume] = useState(false);

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.search}>
        <XtInputSearch
          innerRef={inputRef}
          className={clsSearch}
          placeholder={t("trade.search2")}
          value={keyword}
          onInput={(val) => seKeyword(val)}
          onFocus={handleSearchInputFocus}
          onBlur={handleSearchInputBlur}
        />
      </div>

      {isSearchInputFocus !== undefined && (
        <CMPT_TopSearch
          className={styles.topSearch}
          visible={isTopSearchVisible}
          setSearchInputFocus={setSearchInputFocus}
          isShowVolume={isShowVolume}
          isCfd={isCfd}
          isFiatContract={isFiatContract}
          activeFiatQuote={activeFiatQuote}
        />
      )}

      <div className={styles.content}>
        {!keyword && <CMPT_Tabs tabCfg={tabCfg} tabChange={onTabChange} />}
        <CMPT_Sort sortBy={sortBy} onChange={(item) => setSortBy(item)} isShowVolume={isShowVolume} setIsShowVolume={setIsShowVolume} />

        <CMPT_Container keyword={keyword} tabCfg={tabCfg} sortBy={sortBy} isShowVolume={isShowVolume} onMarketSelect={onMarketSelect} />
      </div>
    </div>
  );
};

export default observer(Main);
